import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { getApiErrorMessage } from '../../utils/apiError';

const InviteUsers = () => {
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);

  // Fetch sent invitations on component mount
  const fetchSentInvitations = useCallback(async () => {
    try {
      setIsLoadingInvitations(true);
      setError('');

      const response = await axiosInstance.get('/users/list_sent_invitations');

      if (response.code === 200) {
        const formattedInvitations = response.data.map(invitation => ({
          id: invitation.id,
          email: invitation.email,
          isMailSent: invitation.is_mail_send,
          status: invitation.is_mail_send ? 'SENT' : 'FAILED',
        }));
        setInvitations(formattedInvitations);
      }
    } catch (err) {
      console.error('Error fetching sent invitations:', err);
      setError('Failed to fetch sent invitations. Please try again.');
    } finally {
      setIsLoadingInvitations(false);
    }
  }, []);

  // Load invitations when component mounts
  useEffect(() => {
    fetchSentInvitations();
  }, [fetchSentInvitations]);

  const handleAddEmail = () => {
    if (emailInput && validateEmail(emailInput) && !emails.includes(emailInput)) {
      setEmails([...emails, emailInput]);
      setEmailInput('');
      setError(''); // Clear any previous errors
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddEmail();
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const handleSendInvitations = async () => {
    if (emails.length === 0) return;

    try {
      setLoading(true);
      setError('');

      const response = await axiosInstance.post('/users/send_invitation', {
        email_address: emails
      });

      if (response.code === 200) {
        // Clear the email input list
        setEmails([]);

        // Show success message
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);

        // Refresh the invitations list
        await fetchSentInvitations();
      } else {
        throw new Error(response.message || 'Failed to send invitations');
      }
    } catch (err) {
      console.error('Error sending invitations:', err);
      setError(getApiErrorMessage(err, 'Failed to send invitations. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (email) => {
    try {
      setError('');

      const response = await axiosInstance.post('/users/resend_invitation', {
        email_address: email
      });

      if (response.code === 200) {
        // Show success message briefly
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);

        // Update the specific invitation status
        setInvitations(prevInvitations =>
          prevInvitations.map(inv =>
            inv.email === email
              ? {
                ...inv,
                isMailSent: response.is_email_send.is_email_send,
                status: response.is_email_send.is_email_send ? 'RESENT' : 'FAILED',
              }
              : inv
          )
        );
      } else {
        throw new Error(response.message || 'Failed to resend invitation');
      }
    } catch (err) {
      console.error('Error resending invitation:', err);
      setError(getApiErrorMessage(err, `Failed to resend invitation to ${email}`));
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  return (

    <div className="settings-section" >
      <div className="settings-section-intro">
        <h2 className="settings-section-title">Members</h2>
        <p className="settings-subtitle">Invite teammates and manage workspace access.</p>
      </div>

      {/* Invitation Form */}
      <div className="p-6">
        <div className="mb-4">
          <p className="text-gray-600 mb-3">Enter user's email and press Enter</p>

          {/* Email Input and Tags */}
          <div className="border border-gray-300 rounded-lg p-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
               {emails.map((email, index) => (
                <div key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center">
                  {email}
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="ml-2 text-blue-600 hover:text-blue-900"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              ))}
 
            <div className="flex">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="user@example.com"
                className="flex-grow px-3 py-2 outline-none border-none"
              />
              <button
                onClick={handleAddEmail}
                disabled={!emailInput}
                className={`px-4 flex items-center gap-2 text-white font-medium rounded transition-colors
    ${emailInput ? 'btn btn-primary opacity-100 cursor-pointer' : 'bg-gray-400 opacity-100 cursor-not-allowed'}`}
              >

                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="create-project-icon">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add
              </button>
            </div>
          </div>

          <button
            onClick={handleSendInvitations}
            disabled={emails.length === 0 || loading}
            className={`w-full mt-4 py-3 rounded-lg font-medium flex items-center justify-center ${emails.length > 0 && !loading
              ? 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800'
              : 'bg-gray-400 cursor-not-allowed'
              } text-white transition-all`}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Sending...
              </>
            ) : (
              <>
                Send Invitation{emails.length !== 1 ? 's' : ''} ({emails.length})
              </>
            )}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center">
            <i className="fas fa-exclamation-circle mr-2"></i>
            {error}
          </div>
        )}

        {/* Success Message */}
        {showSuccess && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg flex items-center">
            <i className="fas fa-check-circle mr-2"></i>
            Invitations sent successfully!
          </div>
        )}

        {/* Invitations List */}
        <div className="border-t pt-4">
          {isLoadingInvitations ? (
            <div className="text-center py-6">
              <i className="fas fa-spinner fa-spin text-2xl text-blue-500 mb-3"></i>
              <p className="text-gray-600">Loading sent invitations...</p>
            </div>
          ) : invitations.length > 0 ? (
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Sent Invitations ({invitations.length})</h3>
              <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                {invitations.map(invitation => (
                  <div key={invitation.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                      {/* Email Column */}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 truncate" title={invitation.email}>
                          {invitation.email}
                        </p>
                        <span className="text-gray-500 text-xs">{invitation.date}</span>
                      </div>

                      {/* Mail Status Column */}
                      <div className="flex justify-center md:justify-start">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${invitation.isMailSent
                          ? invitation.status === 'RESENT'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          <i className={`fas ${invitation.isMailSent
                            ? invitation.status === 'RESENT'
                              ? 'fa-redo'
                              : 'fa-paper-plane'
                            : 'fa-exclamation-triangle'
                            } mr-1`}></i>
                          {invitation.isMailSent
                            ? invitation.status === 'RESENT'
                              ? 'RESENT'
                              : 'SENT'
                            : 'FAILED'
                          }
                        </span>
                      </div>

                      {/* Action Column */}
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleResend(invitation.email)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center px-3 py-1 rounded-md hover:bg-blue-50 transition-colors"
                          title={`Resend invitation to ${invitation.email}`}
                        >
                          <i className="fas fa-redo mr-1"></i> Resend
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <i className="fas fa-envelope-open-text text-4xl text-gray-300 mb-3"></i>
              <h3 className="text-lg font-medium text-gray-700">No Invitations Found</h3>
              <p className="text-gray-500 mt-1 text-sm">There are no email invitations to display at this time.</p>
            </div>
          )}
        </div>
      </div>
    </div>

  );
};

export default InviteUsers;